import { observable, reaction, runInAction } from "mobx";
import { initComponentStore } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { AgentSessionStore } from "@/app_store/agent/AgentSession.generated";
import { TabSettingsData } from "@/app_store/agent/TabSettings.generated";
import { v4 as uuidv4 } from "uuid";

// DraftTabSettings is the same shape as the agent-side TabSettings value type.
// All fields are nullable: null means "not locally overridden — fall through to
// the server value (or account default)". draft_prompt and position are also
// nullable (null = empty / pinned to bottom) so the type stays uniform.
type DraftTabSettings = TabSettingsData;

export class AgentPaneStore {
    draftTabSettings = observable.map<string, DraftTabSettings>();

    renderedRange = { startIndex: 0, endIndex: 0 };
    confirmCloseId: string | null = null;
    sessionCreatedCount: number = 0;
    sessionSettingsId: string | null = null;

    initializingTabIds: string[] = [];
    activeTabId: string | null = null;

    pendingPromptText: string | null = null;

    private _pendingTabSettingsFlush = new Set<string>();

    constructor() {
        initComponentStore(this, { draftTabSettings: true });
        reaction(
            () => this.allTabs.length,
            () => this.selectDefaultTab(),
            { fireImmediately: true },
        );
        this._flushTabSettingsLoop();
    }

    // ── Context ───────────────────────────────────────────────────────────────────

    get contextFilePaths(): string[] {
        return appStore.state.contextFilePaths;
    }

    get isActiveFileInContext(): boolean {
        const path = appStore.state.activeFilePath;
        if (!path) return false;
        return appStore.state.isContextFile(path);
    }

    toggleActiveFileContext() {
        const path = appStore.state.activeFilePath;
        if (!path) return;
        appStore.state.toggleContextFile(path);
    }

    removeContextFile(path: string) {
        appStore.state.removeContextFile(path);
    }

    // ── Connection ────────────────────────────────────────────────────────────────

    get isConnected(): boolean {
        return appStore.agent.isConnected;
    }

    // ── Session list ──────────────────────────────────────────────────────────────

    get agentSessions(): AgentSessionStore[] {
        return appStore.agent.session.project?.agent_sessions ?? [];
    }

    get allTabs(): Array<{ id: string; session: AgentSessionStore | null }> {
        const sessionIds = new Set(this.agentSessions.map(s => s.id));
        const initializing = this.initializingTabIds
            .filter(id => !sessionIds.has(id))
            .map(id => ({ id, session: null as AgentSessionStore | null }));
        const sessions = this.agentSessions.map(s => ({ id: s.id, session: s }));
        return [...sessions, ...initializing];
    }

    get isTabInitializing(): boolean {
        return this.initializingTabIds.includes(this.activeTabId!);
    }

    selectDefaultTab() {
        if (this.activeTabId) return;
        const tabs = this.allTabs;
        if (tabs.length > 0) this.activeTabId = tabs[tabs.length - 1].id;
    }

    get activeSession(): AgentSessionStore | null {
        const tabId = this.activeTabId;
        if (!tabId) return null;
        return this.agentSessions.find(s => s.id === tabId) ?? null;
    }

    get isStreaming(): boolean {
        return this.activeSession?.is_streaming ?? false;
    }

    get thinkingDots(): number {
        return this.activeSession?.thinking_dots ?? 0;
    }

    get isInitialized(): boolean {
        return (this.activeSession?.flat_history.length ?? 0) > 0 || this.pendingPromptText !== null;
    }

    // ── Tab settings (merged server + draft) ─────────────────────────────────────

    private ensureDraft(id: string): DraftTabSettings {
        if (!this.draftTabSettings.has(id)) {
            const ts = this.agentSessions.find(s => s.id === id)?.tab_settings;
            this.draftTabSettings.set(id, {
                draft_prompt: ts?.draft_prompt ?? null,
                position: null,
                model: null,
                effort: null,
                backend: null,
                readonly_mode: null,
            });
        }
        return this.draftTabSettings.get(id)!;
    }

    // Returns resolved (non-null) settings by merging draft overrides → server
    // tab_settings → account defaults. Type is inferred from the returned object.
    tabSettingsFor(id: string) {
        const session = this.agentSessions.find(s => s.id === id);
        const ts = session?.tab_settings;
        const draft = this.ensureDraft(id);
        const acct = appStore.account.settings;
        return {
            draft_prompt: draft.draft_prompt ?? ts?.draft_prompt ?? "",
            position: draft.position ?? ts?.position ?? null,  // null = bottom
            model: draft.model ?? ts?.model ?? acct?.claude_model ?? "claude-sonnet-4-6",
            effort: draft.effort ?? ts?.effort ?? acct?.claude_effort ?? "",
            backend: draft.backend ?? ts?.backend ?? acct?.claude_backend ?? "json",
            readonly_mode: draft.readonly_mode ?? ts?.readonly_mode ?? false,
        };
    }

    get tabSettings() {
        if (!this.activeTabId) return null;
        return this.tabSettingsFor(this.activeTabId);
    }

    // ── Prompt ────────────────────────────────────────────────────────────────────

    get prompt(): string {
        return this.tabSettings?.draft_prompt ?? "";
    }

    changePrompt(value: string) {
        if (!this.activeTabId) return;
        this.ensureDraft(this.activeTabId).draft_prompt = value;
        this._pendingTabSettingsFlush.add(this.activeTabId);
    }

    async sendPrompt() {
        const text = this.prompt.trim();
        const session = this.activeSession;
        if (!text || this.isStreaming || !this.isConnected || !session) return;

        const tabId = this.activeTabId!;
        this.ensureDraft(tabId).draft_prompt = null;
        this._pendingTabSettingsFlush.add(tabId);
        await this._flushDraft(tabId);

        runInAction(() => {
            this.pendingPromptText = text;
        });

        await session.run_prompt(text, this.contextFilePaths.length > 0 ? this.contextFilePaths : null);

        runInAction(() => {
            this.pendingPromptText = null;
        });
    }

    // ── Session settings ──────────────────────────────────────────────────────────

    async updateSessionSettings(id: string, patch: Partial<Pick<TabSettingsData, "model" | "effort" | "backend" | "readonly_mode">>) {
        const session = this.agentSessions.find(s => s.id === id);
        if (!session) return;
        const current = this.tabSettingsFor(id);
        const draft = this.ensureDraft(id);
        // Optimistic update so the UI reflects the change immediately.
        runInAction(() => {
            if (patch.model !== undefined) draft.model = patch.model;
            if (patch.effort !== undefined) draft.effort = patch.effort;
            if (patch.backend !== undefined) draft.backend = patch.backend;
            if (patch.readonly_mode !== undefined) draft.readonly_mode = patch.readonly_mode;
        });
        await session.update_settings(
            patch.model ?? current.model,
            patch.effort ?? current.effort,
            patch.backend ?? current.backend,
            patch.readonly_mode ?? current.readonly_mode,
            session.tab_settings?.draft_prompt ?? null,
            session.tab_settings?.position ?? null,
        );
        // Server patch has arrived (protocol guarantee) — clear draft so server is authoritative.
        runInAction(() => {
            if (patch.model !== undefined) draft.model = null;
            if (patch.effort !== undefined) draft.effort = null;
            if (patch.backend !== undefined) draft.backend = null;
            if (patch.readonly_mode !== undefined) draft.readonly_mode = null;
        });
    }

    private async _flushTabSettingsLoop() {
        while (true) {
            await new Promise<void>(resolve => setTimeout(resolve, 2000));
            for (const session of this.agentSessions) {
                if (this._pendingTabSettingsFlush.has(session.id)) {
                    await this._flushDraft(session.id);
                }
            }
        }
    }

    private async _flushDraft(id: string) {
        const session = this.agentSessions.find(s => s.id === id);
        if (!session) return;
        const ts = session.tab_settings;
        const draft = this.draftTabSettings.get(id);
        this._pendingTabSettingsFlush.delete(id);
        await session.update_settings(
            ts?.model ?? null,
            ts?.effort ?? null,
            ts?.backend ?? null,
            ts?.readonly_mode ?? null,
            draft?.draft_prompt ?? null,
            draft?.position ?? null,
        );
    }

    get activeSessionBackend(): string {
        return this.tabSettings?.backend ?? appStore.account.settings?.claude_backend ?? "json";
    }

    openSessionSettings(id: string) {
        this.sessionSettingsId = id;
    }

    closeSessionSettings() {
        this.sessionSettingsId = null;
    }

    get readonlyMode(): boolean {
        return this.tabSettings?.readonly_mode ?? false;
    }

    async toggleReadonlyMode() {
        if (!this.activeTabId) return;
        const next = !this.tabSettings?.readonly_mode;
        await this.updateSessionSettings(this.activeTabId, { readonly_mode: next });
    }

    // ── Scroll ────────────────────────────────────────────────────────────────────

    get lastUserItemIndex(): number {
        const items = this.activeSession?.flat_history ?? [];
        for (let i = items.length - 1; i >= 0; i--) {
            if (items[i].type === "user") return i;
        }
        return -1;
    }

    get lastUserItemContent(): string | null {
        const idx = this.lastUserItemIndex;
        return idx >= 0 ? (this.activeSession?.flat_history[idx].content ?? null) : null;
    }

    get promptBannerPosition(): "top" | "bottom" | null {
        const idx = this.lastUserItemIndex;
        if (idx < 0) return null;
        if (idx < this.renderedRange.startIndex) return "top";
        if (idx > this.renderedRange.endIndex) return "bottom";
        return null;
    }

    // null = pinned to bottom; number = specific virtuoso index to restore on switch
    get position(): number | null {
        return this.tabSettings?.position ?? null;
    }

    get isAtBottom(): boolean {
        return this.position === null;
    }

    changePosition(value: number | null) {
        if (!this.activeTabId) return;
        this.ensureDraft(this.activeTabId).position = value;
        this._pendingTabSettingsFlush.add(this.activeTabId);
    }

    changeRenderedRange(range: { startIndex: number; endIndex: number }) {
        this.renderedRange = range;
    }

    // ── Session actions ───────────────────────────────────────────────────────────

    changeConfirmCloseId(id: string | null) {
        this.confirmCloseId = id;
    }

    selectClaudeSession(id: string) {
        this.activeTabId = id;
        this.pendingPromptText = null;
        this.renderedRange = { startIndex: 0, endIndex: 0 };
    }

    async createClaudeSession() {
        const project = appStore.agent.session.project;
        if (!project) return;

        const id = uuidv4();

        runInAction(() => {
            this.initializingTabIds.push(id);
            this.draftTabSettings.set(id, { draft_prompt: null, position: null, model: null, effort: null, backend: null, readonly_mode: null });
            this.activeTabId = id;
            this.sessionCreatedCount++;
            this.pendingPromptText = null;
        });

        const result = await project.create_agent_session("json", id);

        let sessionReady = false;
        runInAction(() => {
            const idx = this.initializingTabIds.indexOf(id);
            if (idx < 0) {
                // Tab was closed while RPC was in flight — destroy the created session
                if (result) appStore.agent.session.project?.destroy_agent_session(id);
                return;
            }
            this.initializingTabIds.splice(idx, 1);
            if (!result) {
                this.draftTabSettings.delete(id);
                if (this.activeTabId === id) this.activeTabId = null;
            } else {
                sessionReady = true;
            }
        });

        if (sessionReady) {
            const defaultModel = appStore.account.settings?.claude_model ?? "claude-sonnet-4-6";
            await this.updateSessionSettings(id, { model: defaultModel });
        }
    }

    closeClaudeSession(id: string) {
        this.draftTabSettings.delete(id);
        this._pendingTabSettingsFlush.delete(id);
        if (this.activeTabId === id) {
            this.pendingPromptText = null;
            const remaining = this.allTabs.filter(t => t.id !== id);
            this.activeTabId = remaining.length > 0 ? remaining[remaining.length - 1].id : null;
        }

        const initIdx = this.initializingTabIds.indexOf(id);
        if (initIdx >= 0) {
            // createClaudeSession will see id missing and destroy the session on return
            this.initializingTabIds.splice(initIdx, 1);
            return;
        }

        appStore.agent.session.project?.destroy_agent_session(id);
    }

    interruptClaudeSession() {
        this.activeSession?.interrupt();
    }

    // ── Open File In ───────────────────────────────────────────────────────────────

    openFileInCode(relativePath: string) {
        appStore.state.requestOpenInCode(relativePath);
        appStore.state.requestSection("code");
    }

    openFileInReview(relativePath: string) {
        appStore.state.requestOpenInReview(relativePath);
        appStore.state.requestSection("review");
    }

}
